import type {
  ContextCompactedInfo,
  ConversationMessageRow,
  ConversationRun
} from '@axilon-apps/domains-ontology-ui-state'

import { formatContextCompactedNotice } from '../../lib/model-policy-labels'

/**
 * Debug-task-summary line for a turn that compacted. The public chat marker
 * stays on the in-memory `context_compacted` event. This label is the same
 * sentence, with a provider-less fallback when a persisted block omitted one.
 */
export function formatDebugCompactionNotice(info: ContextCompactedInfo): string {
  if (info.mode !== 'emulated' && info.provider.trim().length === 0) {
    return 'Earlier conversation summarized'
  }
  return formatContextCompactedNotice(info)
}

/**
 * Compaction the debug summary can show after refresh. The live event wins
 * when this session still has it (model, reason, window fill). Otherwise the
 * last persisted assistant `compaction` block on this run or a child run is
 * enough to say who summarized. The block's summary text stays out of the
 * result.
 */
export function compactionInfoFromRun(turn: ConversationRun): ContextCompactedInfo | null {
  if (turn.contextCompacted) return turn.contextCompacted
  const found = scanRun(turn, null)
  if (!found) return null
  return {
    provider: found.provider,
    model: '',
    mode: found.mode,
    reason: 'threshold'
  }
}

interface PersistedCompaction {
  provider: string
  mode: 'native' | 'emulated'
}

function scanRun(
  turn: ConversationRun,
  found: PersistedCompaction | null
): PersistedCompaction | null {
  found = scanMessages(turn.messages, found)
  for (const child of turn.children ?? []) {
    found = scanRun(child, found)
  }
  return found
}

function scanMessages(
  messages: ConversationMessageRow[] | undefined,
  found: PersistedCompaction | null
): PersistedCompaction | null {
  if (!Array.isArray(messages)) return found
  for (const message of messages) {
    if (!Array.isArray(message?.content)) continue
    for (const block of message.content) {
      const next = compactionFromBlock(block)
      if (next) found = next
    }
  }
  return found
}

function compactionFromBlock(block: unknown): PersistedCompaction | null {
  if (!block || typeof block !== 'object') return null
  const rec = block as Record<string, unknown>
  if (rec.type !== 'compaction') return null
  const encrypted = typeof rec.encrypted_content === 'string' && rec.encrypted_content.length > 0
  const content = typeof rec.content === 'string' && rec.content.length > 0
  if (!encrypted && !content) return null
  const provider = typeof rec.provider === 'string' ? rec.provider : ''
  return { provider, mode: provider === 'axilon' ? 'emulated' : 'native' }
}
